import { create } from 'zustand';

export type FloatingDockState = 'floating' | 'left' | 'right' | 'top' | 'bottom';

export interface FloatingWindowRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface FloatingWindowRuntimeConfig {
  id: string;
  title: string;
  group?: string;
  defaultRect: FloatingWindowRect;
  defaultOpen?: boolean;
  canMaximize?: boolean;
  keyboardClosable?: boolean;
  important?: boolean;
  helpText?: string;
  onOpen?: () => void;
  onClose?: () => void;
  onMinimize?: () => void;
  onRestore?: () => void;
}

export interface FloatingWindowState {
  id: string;
  open: boolean;
  minimized: boolean;
  maximized: boolean;
  pinned: boolean;
  dock: FloatingDockState;
  rect: FloatingWindowRect;
  zIndex: number;
  lastTouchedAt: number;
}

interface PersistedFloatingWorkspace {
  windows: Record<string, FloatingWindowState>;
  previousWorkspace: Record<string, FloatingWindowState> | null;
  zCounter: number;
  lastClosedId: string | null;
}

interface FloatingWindowStore {
  scopeKey: string;
  configs: Record<string, FloatingWindowRuntimeConfig>;
  windows: Record<string, FloatingWindowState>;
  previousWorkspace: Record<string, FloatingWindowState> | null;
  zCounter: number;
  activeWindowId: string | null;
  lastClosedId: string | null;
  setScope: (userId?: string | null, projectId?: string | null) => void;
  restoreWorkspaceState: (workspace: Partial<PersistedFloatingWorkspace>) => void;
  registerWindow: (config: FloatingWindowRuntimeConfig) => void;
  unregisterWindow: (id: string) => void;
  openWindow: (id: string) => void;
  closeWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  restoreWindow: (id: string) => void;
  bringToFront: (id: string) => void;
  togglePinned: (id: string) => void;
  toggleMaximized: (id: string) => void;
  resetWindow: (id: string) => void;
  dockWindow: (id: string, dock: FloatingDockState) => void;
  updateGeometry: (id: string, rect: FloatingWindowRect, snap?: boolean) => void;
  nudgeActiveWindow: (dx: number, dy: number) => void;
  closeAllFloatingWindows: () => void;
  restorePreviousWorkspace: () => void;
  quickRestore: () => void;
}

const STORAGE_PREFIX = 'r2-floating-window-workspace-v1';
const SNAP = 14;
const MIN_WIDTH = 180;
const MIN_HEIGHT = 92;

function canUseWindow(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

function scopeStorageKey(scopeKey: string): string {
  return `${STORAGE_PREFIX}:${scopeKey}`;
}

function safeScopePart(value?: string | null): string {
  return (value || 'default').replace(/[^a-zA-Z0-9._@-]/g, '_');
}

function defaultScopeKey(): string {
  if (!canUseWindow()) return 'guest:workspace';
  return `${safeScopePart(localStorage.getItem('r2:lastLoginEmail'))}:${safeScopePart(localStorage.getItem('projectContext.activeProjectId'))}`;
}

function clampRect(rect: FloatingWindowRect): FloatingWindowRect {
  if (!canUseWindow()) return rect;
  const width = Math.max(MIN_WIDTH, Math.min(rect.width, window.innerWidth - 16));
  const height = Math.max(MIN_HEIGHT, Math.min(rect.height, window.innerHeight - 16));
  const x = Math.max(0, Math.min(rect.x, window.innerWidth - width));
  const y = Math.max(0, Math.min(rect.y, window.innerHeight - height));
  return { x, y, width, height };
}

function dockRect(dock: FloatingDockState, rect: FloatingWindowRect): FloatingWindowRect {
  if (!canUseWindow() || dock === 'floating') return clampRect(rect);
  const margin = 12;
  const topChrome = 58;
  const bottomChrome = 34;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (dock === 'left') return { x: margin, y: topChrome, width: Math.max(320, Math.min(460, vw * 0.32)), height: vh - topChrome - bottomChrome - margin };
  if (dock === 'right') {
    const width = Math.max(320, Math.min(460, vw * 0.32));
    return { x: vw - width - margin, y: topChrome, width, height: vh - topChrome - bottomChrome - margin };
  }
  if (dock === 'top') return { x: margin, y: topChrome, width: vw - margin * 2, height: Math.max(220, Math.min(360, vh * 0.34)) };
  const height = Math.max(220, Math.min(360, vh * 0.34));
  return { x: margin, y: vh - height - bottomChrome, width: vw - margin * 2, height };
}

function snapRect(rect: FloatingWindowRect, id: string, windows: Record<string, FloatingWindowState>): FloatingWindowRect {
  if (!canUseWindow()) return rect;
  let next = clampRect(rect);
  const right = next.x + next.width;
  const bottom = next.y + next.height;
  if (Math.abs(next.x) < SNAP) next = { ...next, x: 0 };
  if (Math.abs(next.y) < SNAP) next = { ...next, y: 0 };
  if (Math.abs(window.innerWidth - right) < SNAP) next = { ...next, x: window.innerWidth - next.width };
  if (Math.abs(window.innerHeight - bottom) < SNAP) next = { ...next, y: window.innerHeight - next.height };

  Object.values(windows).forEach((other) => {
    if (other.id === id || !other.open || other.minimized) return;
    const o = other.rect;
    const oRight = o.x + o.width;
    const oBottom = o.y + o.height;
    if (Math.abs(next.x - oRight) < SNAP) next = { ...next, x: oRight };
    if (Math.abs(next.x + next.width - o.x) < SNAP) next = { ...next, x: o.x - next.width };
    if (Math.abs(next.y - oBottom) < SNAP) next = { ...next, y: oBottom };
    if (Math.abs(next.y + next.height - o.y) < SNAP) next = { ...next, y: o.y - next.height };
  });
  return clampRect(next);
}

function createInitialState(config: FloatingWindowRuntimeConfig, zIndex: number): FloatingWindowState {
  return {
    id: config.id,
    open: !!config.defaultOpen,
    minimized: false,
    maximized: false,
    pinned: false,
    dock: 'floating',
    rect: clampRect(config.defaultRect),
    zIndex,
    lastTouchedAt: Date.now(),
  };
}

function loadWorkspace(scopeKey: string): PersistedFloatingWorkspace {
  if (!canUseWindow()) return { windows: {}, previousWorkspace: null, zCounter: 30000, lastClosedId: null };
  try {
    const raw = localStorage.getItem(scopeStorageKey(scopeKey));
    if (!raw) return { windows: {}, previousWorkspace: null, zCounter: 30000, lastClosedId: null };
    const parsed = JSON.parse(raw) as PersistedFloatingWorkspace;
    return {
      windows: parsed.windows ?? {},
      previousWorkspace: parsed.previousWorkspace ?? null,
      zCounter: parsed.zCounter ?? 30000,
      lastClosedId: parsed.lastClosedId ?? null,
    };
  } catch {
    return { windows: {}, previousWorkspace: null, zCounter: 30000, lastClosedId: null };
  }
}

function saveWorkspace(scopeKey: string, workspace: PersistedFloatingWorkspace): void {
  if (!canUseWindow()) return;
  try { localStorage.setItem(scopeStorageKey(scopeKey), JSON.stringify(workspace)); } catch { /* storage quota */ }
}

function persistState(state: FloatingWindowStore): void {
  saveWorkspace(state.scopeKey, {
    windows: state.windows,
    previousWorkspace: state.previousWorkspace,
    zCounter: state.zCounter,
    lastClosedId: state.lastClosedId,
  });
}

export const useFloatingWindowStore = create<FloatingWindowStore>((set, get) => {
  const initialScope = defaultScopeKey();
  const loaded = loadWorkspace(initialScope);
  return {
    scopeKey: initialScope,
    configs: {},
    windows: loaded.windows,
    previousWorkspace: loaded.previousWorkspace,
    zCounter: loaded.zCounter,
    activeWindowId: null,
    lastClosedId: loaded.lastClosedId,

    setScope(userId, projectId) {
      const nextScope = `${safeScopePart(userId)}:${safeScopePart(projectId)}`;
      const current = get();
      if (nextScope === current.scopeKey) return;
      persistState(current);
      const next = loadWorkspace(nextScope);
      set({
        scopeKey: nextScope,
        windows: next.windows,
        previousWorkspace: next.previousWorkspace,
        zCounter: next.zCounter,
        activeWindowId: null,
        lastClosedId: next.lastClosedId,
      });
    },

    restoreWorkspaceState(workspace) {
      set((s) => {
        const next = {
          ...s,
          windows: workspace.windows ?? s.windows,
          previousWorkspace: workspace.previousWorkspace ?? s.previousWorkspace,
          zCounter: workspace.zCounter ?? s.zCounter,
          lastClosedId: workspace.lastClosedId ?? s.lastClosedId,
        };
        persistState(next);
        return next;
      });
    },

    registerWindow(config) {
      set((s) => {
        const existing = s.windows[config.id];
        const windows = existing ? s.windows : { ...s.windows, [config.id]: createInitialState(config, s.zCounter + 1) };
        const next = {
          ...s,
          configs: { ...s.configs, [config.id]: config },
          windows,
          zCounter: existing ? s.zCounter : s.zCounter + 1,
        };
        persistState(next);
        return next;
      });
    },

    unregisterWindow(id) {
      set((s) => {
        const cfg = s.configs[id];
        if (!cfg || cfg.important) return s;
        const configs = { ...s.configs };
        delete configs[id];
        return { configs };
      });
    },

    openWindow(id) {
      const cfg = get().configs[id];
      set((s) => {
        const zCounter = s.zCounter + 1;
        const existing = s.windows[id] ?? createInitialState(cfg ?? { id, title: id, defaultRect: { x: 80, y: 80, width: 360, height: 280 } }, zCounter);
        const nextWindow = { ...existing, open: true, minimized: false, zIndex: zCounter, lastTouchedAt: Date.now() };
        const next = { ...s, windows: { ...s.windows, [id]: nextWindow }, zCounter, activeWindowId: id };
        persistState(next);
        return next;
      });
      cfg?.onOpen?.();
    },

    closeWindow(id) {
      const cfg = get().configs[id];
      set((s) => {
        const existing = s.windows[id];
        if (!existing) return s;
        const nextWindow = { ...existing, open: false, minimized: false, lastTouchedAt: Date.now() };
        const next = {
          ...s,
          windows: { ...s.windows, [id]: nextWindow },
          activeWindowId: s.activeWindowId === id ? null : s.activeWindowId,
          lastClosedId: id,
        };
        persistState(next);
        return next;
      });
      cfg?.onClose?.();
    },

    minimizeWindow(id) {
      const cfg = get().configs[id];
      set((s) => {
        const existing = s.windows[id];
        if (!existing) return s;
        const nextWindow = { ...existing, open: true, minimized: true, lastTouchedAt: Date.now() };
        const next = { ...s, windows: { ...s.windows, [id]: nextWindow }, activeWindowId: s.activeWindowId === id ? null : s.activeWindowId, lastClosedId: id };
        persistState(next);
        return next;
      });
      cfg?.onMinimize?.();
    },

    restoreWindow(id) {
      const cfg = get().configs[id];
      set((s) => {
        const zCounter = s.zCounter + 1;
        const existing = s.windows[id] ?? createInitialState(cfg ?? { id, title: id, defaultRect: { x: 80, y: 80, width: 360, height: 280 } }, zCounter);
        const nextWindow = { ...existing, open: true, minimized: false, zIndex: zCounter, lastTouchedAt: Date.now() };
        const next = { ...s, windows: { ...s.windows, [id]: nextWindow }, zCounter, activeWindowId: id };
        persistState(next);
        return next;
      });
      cfg?.onRestore?.();
      cfg?.onOpen?.();
    },

    bringToFront(id) {
      set((s) => {
        const existing = s.windows[id];
        if (!existing) return s;
        const zCounter = s.zCounter + 1;
        const nextWindow = { ...existing, zIndex: zCounter, lastTouchedAt: Date.now() };
        const next = { ...s, windows: { ...s.windows, [id]: nextWindow }, zCounter, activeWindowId: id };
        persistState(next);
        return next;
      });
    },

    togglePinned(id) {
      set((s) => {
        const existing = s.windows[id];
        if (!existing) return s;
        const next = { ...s, windows: { ...s.windows, [id]: { ...existing, pinned: !existing.pinned, lastTouchedAt: Date.now() } }, activeWindowId: id };
        persistState(next);
        return next;
      });
    },

    toggleMaximized(id) {
      set((s) => {
        const existing = s.windows[id];
        if (!existing) return s;
        const nextWindow: FloatingWindowState = { ...existing, maximized: !existing.maximized, minimized: false, dock: 'floating', lastTouchedAt: Date.now() };
        const next = { ...s, windows: { ...s.windows, [id]: nextWindow }, activeWindowId: id };
        persistState(next);
        return next;
      });
    },

    resetWindow(id) {
      const cfg = get().configs[id];
      if (!cfg) return;
      set((s) => {
        const existing = s.windows[id];
        const nextWindow: FloatingWindowState = {
          ...(existing ?? createInitialState(cfg, s.zCounter + 1)),
          rect: clampRect(cfg.defaultRect),
          dock: 'floating',
          maximized: false,
          minimized: false,
          open: true,
          lastTouchedAt: Date.now(),
        };
        const next = { ...s, windows: { ...s.windows, [id]: nextWindow }, activeWindowId: id };
        persistState(next);
        return next;
      });
      cfg.onRestore?.();
    },

    dockWindow(id, dock) {
      set((s) => {
        const existing = s.windows[id];
        if (!existing) return s;
        const rect = dockRect(dock, existing.rect);
        const next = { ...s, windows: { ...s.windows, [id]: { ...existing, rect, dock, maximized: false, lastTouchedAt: Date.now() } }, activeWindowId: id };
        persistState(next);
        return next;
      });
    },

    updateGeometry(id, rect, snap = true) {
      set((s) => {
        const existing = s.windows[id];
        if (!existing) return s;
        const nextRect = snap ? snapRect(rect, id, s.windows) : clampRect(rect);
        const nextWindow: FloatingWindowState = { ...existing, rect: nextRect, maximized: false, dock: 'floating', lastTouchedAt: Date.now() };
        const next = { ...s, windows: { ...s.windows, [id]: nextWindow }, activeWindowId: id };
        persistState(next);
        return next;
      });
    },

    nudgeActiveWindow(dx, dy) {
      const id = get().activeWindowId;
      if (!id) return;
      const win = get().windows[id];
      if (!win || !win.open || win.minimized || win.maximized) return;
      get().updateGeometry(id, { ...win.rect, x: win.rect.x + dx, y: win.rect.y + dy }, true);
    },

    closeAllFloatingWindows() {
      set((s) => {
        const previousWorkspace = { ...s.windows };
        const windows = Object.fromEntries(Object.entries(s.windows).map(([id, win]) => [id, id === 'window-manager' ? win : { ...win, open: false, minimized: false }])) as Record<string, FloatingWindowState>;
        const next = { ...s, windows, previousWorkspace, activeWindowId: 'window-manager' };
        persistState(next);
        return next;
      });
    },

    restorePreviousWorkspace() {
      set((s) => {
        if (!s.previousWorkspace) return s;
        const next = { ...s, windows: s.previousWorkspace, activeWindowId: null };
        persistState(next);
        return next;
      });
    },

    quickRestore() {
      const id = get().lastClosedId;
      if (id) get().restoreWindow(id);
      else get().restorePreviousWorkspace();
    },
  };
});
